import { Button, OutlinedInput, Stack, Switch, Autocomplete, Popper, CircularProgress } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import { ingestService } from "../../../Core/Api/services";
import { GridColDef } from "@mui/x-data-grid";
import TableCellBool from "../../Table/TableCellBool";
import DataTable, { CellSize } from "../../Table/table";
import { GroupByToMap } from "../../../Utils/helpers";
import {
    IngestColumnsGrouped,
    createColumns,
    handleMatchColumns,
} from "../../../Pages/admin/Ingest/IngestConfigurePlaceholder/ingest-config-placeholder";
import { IngestConfigPackColumnSet } from "../../../Core/Api/Types/ingest-config-pack-column-set";
import { DbIngestConfigPackPlaceholderRowFieldInfos } from "../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-field-infos";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
    // confirm: boolean;
};

export type ModalExportPlaceholderConfigElementProps = {
    onClose?: (props: CloseClbProps) => void;
    id: number;
    name: string;
};

export type IngestColumnsConfig = {
    columnBackend: IngestConfigPackColumnSet;
    row: DbIngestConfigPackPlaceholderRowFieldInfos;
};

export type ModalExportPlaceholderConfigProps = CommonModalProps<ModalExportPlaceholderConfigElementProps>;

const ModalExportPlaceholderConfig = React.forwardRef<any, ModalExportPlaceholderConfigProps>((props, ref) => {
    const { t } = useTranslation();
    const { closeModal, onClose, id, name } = props;
    const [rows, setRows] = useState<IngestColumnsConfig[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const apiGrid = useGridApiRef();

    const handleExport = () => {
        apiGrid.current.exportDataAsCsv({ delimiter: GlobalConst.EXPORT_DELIMITER });
    };

    useEffect(() => {
        // Load columns set
        ingestService.GetIngestMetadataFields().then((response) => {
            if (!response.columns) return;

            // Group metadata by "group" prop
            let grouped = GroupByToMap(response.columns!, (x) => x.columnFieldInfos?.group);

            let columnsArray: IngestColumnsGrouped[] = [];

            columnsArray = createColumns(grouped);

            ingestService.GetPlaceholderById(id).then((response) => {
                handleMatchColumns(columnsArray, response);
                setRows(columnsArray?.flatMap((x) => x.columns));
                setIsLoading(false);
            });
        });
    }, []);

    const columnsAction: GridColDef[] = [
        {
            field: "fieldName",
            headerName: t("common:field"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            valueGetter: (value: any, row: any) => row?.columnBackend.columnInfos.label,
        },
        {
            field: "mandatory",
            headerName: t("common:mandatory"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            valueGetter: (value: any, row: any) => row?.columnBackend.columnInfos.isMandatory,
            renderCell: (data: any) => <TableCellBool value={data?.row?.columnBackend.columnInfos.isMandatory} />,
        },
        {
            field: "overrideIsNotMandatory",
            headerName: t("table:headerName.overrideNotMandatory"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            valueGetter: (value: any, row: any) => row?.row.overrideIsNotMandatory,
            renderCell: (data: any) => <Switch size="small" value={data.value} checked={data.value} />,
        },
        {
            field: "applicable",
            headerName: t("common:applicable"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            valueGetter: (value: any, row: any) => row?.row.isApplicable,
            renderCell: (data: any) => <Switch size="small" value={data.value} checked={data.value} />,
        },
        {
            field: "values",
            headerName: t("table:headerName.availableValues"),
            flex: CellSize.xxlarge,
            minWidth: CellSize.xxlarge,
            valueGetter: (value: any, row: any) => row?.row.predefinedValues,
            renderCell: (data: any) =>
                data?.row?.columnBackend.columnFieldInfos.values !== null && (
                    <Autocomplete
                        disablePortal
                        id="autocomplete-small"
                        size="medium"
                        options={[]}
                        multiple
                        sx={{ flex: 1 }}
                        value={data?.row?.row.predefinedValues}
                        PopperComponent={(props) => <Popper {...props} placement="auto" />}
                        popupIcon={<SearchIcon />}
                        renderInput={(params) => (
                            <OutlinedInput
                                {...params.InputProps}
                                inputProps={params.inputProps}
                                placeholder={t("field:placeholder.availableValues")}
                                sx={{ width: "100%" }}
                            />
                        )}
                    />
                ),
        },
    ];

    return (
        <Modal
            title={t("modal:title.placeholderConfigurationPreview") + name}
            reference={ref}
            closeModal={closeModal}
            width={1100}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5}>
                        <Button onClick={closeModal}>{t("common:cancel")}</Button>
                    </Stack>
                    <Button onClick={handleExport} variant="contained">
                        {t("button:exportAsCsv")}
                    </Button>
                </Stack>
            }
        >
            {isLoading && (
                <CircularProgress
                    sx={{
                        position: "relative",
                        top: "44%",
                        left: "50%",
                    }}
                />
            )}
            {!isLoading && (
                <Stack>
                    <DataTable
                        noControls
                        rows={rows}
                        columns={columnsAction}
                        rowHeight={54}
                        getRowId={(row: any) => row.columnBackend.columnFieldInfos.id}
                        apiRef={apiGrid}
                    />
                </Stack>
            )}
        </Modal>
    );
});

export default ModalExportPlaceholderConfig;
